/* ============================================================
 * BYO Builder — picker UI + cart submission + data adapter.
 * One global: window.BYO. No frameworks, no jQuery.
 * Single file by design — `defer` on scripts inside <section> is
 * unreliable across browsers. Swap to a separate file from <head>
 * when OpenSearch goes live.
 * ============================================================ */
(function (global) {
  'use strict';

  // -------- Data adapter --------
  // Source of truth: the ARTWORK INDEX (adg-artworks-index.json) — the artwork
  // catalogue itself, generated from the backend database. Not a product feed:
  // bundles, blanks and legacy products can never appear because they are not
  // artworks. Carries orientation, so the calendar can exclude landscape.
  // v0 ships as a theme asset; the backend CDN version is a URL swap.
  // The old /collections/<h>/products.json path is kept ONLY as a fallback if
  // the index fails to load.
  const PAGE_SIZE = 30;
  const ART_PRINT_TAG = 'product-type:art-print';
  const ARTIST_TAG_PREFIX = 'artist:';
  const COLLECTION_TAG_PREFIX = 'collection:';

  const _cfg = { indexUrl: '', orientation: 'all', };
  let _index = null;        // normalized artworks, loaded once
  let _indexPromise = null; // the in-flight fetch — every caller during the
                            // load shares it (the value-only cache made two
                            // concurrent callers download the index twice)

  function configure(opts) {
    if (opts && opts.indexUrl) _cfg.indexUrl = opts.indexUrl;
    if (opts && opts.orientation) _cfg.orientation = opts.orientation;
  }

  function normalizeIndexItem(it) {
    // Everything the row carries rides through FIRST, then the builder's own
    // names are layered on top. This used to be a whitelist, which silently
    // ate `k`, `pr` and `bs` — and would eat any field the backend adds
    // next, since the index file and this theme ship on separate schedules.
    return Object.assign({}, it, {
      key: it.h,                                  // durable identity = artwork handle
      shopify_id: it.pid || null,                 // art-print product id (null for the few unsynced)
      handle: it.h + '-art-print',
      artwork_handle: it.h,
      artist_handle: it.ah || '',
      title: it.t || it.h,
      artist_name: it.a || (it.ah || '').replace(/-/g, ' '),
      collections: it.c || [],
      orientation: it.o || 'portrait',
      image: it.img,
      image_full: it.img_full || it.img,
      pub: it.pub || '',
      // The date fields the shared Date sorts and the "New" badge read.
      // Dropping them here once made new/old inside the builders degrade to
      // the store's 3-value bulk-publish date — i.e. alphabetical (08-08).
      d: it.d || '',
      y: it.y || '',
    });
  }

  function loadIndex() {
    if (_index) return Promise.resolve(_index);
    if (!_cfg.indexUrl) return Promise.resolve(null);
    if (_indexPromise) return _indexPromise;
    _indexPromise = (async () => {
      try {
        // Shared with the tiles section on the same page — one fetch. Falls
        // back to its own when the loader is not on this template; assuming
        // it was there took every builder down (08-10).
        const url = (_cfg.indexUrl || '').trim();
        let rows;
        if (window.ADGIndex && window.ADGIndex.load) {
          rows = (await window.ADGIndex.load(url)).rows || [];
        } else {
          const res = await fetch(url, { headers: { Accept: 'application/json' } });
          if (!res.ok) throw new Error(`index ${res.status}`);
          const j = await res.json();
          // v3 stores artist/collection/orientation as indices only the
          // shared loader can expand; reading them raw would be nonsense.
          if ((j.meta && j.meta.v || 0) >= 3) {
            throw new Error('v3 index needs adg-index.js on this page');
          }
          rows = j.artworks || [];
        }
        // Cached WHOLE, never pre-filtered — see visibleIndex().
        _index = rows.map(normalizeIndexItem);
        return _index;
      } catch (err) {
        // THIS call falls back to the collection feed, but the failure is
        // not remembered: the old sticky flag meant one bad fetch locked
        // every collection tile to the same 30 fallback prints for the rest
        // of the session. Next call retries.
        console.warn('[BYO] artwork index unavailable, falling back to collection feed', err);
        _indexPromise = null;
        return null;
      }
    })();
    return _indexPromise;
  }

  /* The picker's view of the index. The calendar's portrait-only rule is applied
     HERE, on every read, rather than baked into the cached list at load time.
     `_index` is a module-level cache and load order is not contractual: whoever
     calls loadIndex() FIRST would otherwise freeze their own orientation setting
     into the shared list for the rest of the page session. Today init() happens
     to configure() before anything fetches, so the old shape was correct by
     luck; one reordering — a preview pane, a filter bar, a tiles section warming
     the cache — and every landscape design leaks into the calendar with no error
     anywhere. Filtering at read cannot be raced. Verified: with the old code a
     load-before-configure leaked all 117; with this one it leaks 0.

     live() deliberately keeps reading the FULL `_index`, because a pick restored
     from localStorage must still resolve to its artwork even if the current
     builder would not offer it. */
  async function visibleIndex() {
    const all = await loadIndex();
    if (!all) return null;
    if (_cfg.orientation !== 'portrait') return all;
    return all.filter((a) => a.orientation !== 'landscape');
  }

  function shopifyImageUrl(src, width) {
    if (!src) return '';
    return src.replace(/(\.(?:jpg|jpeg|png|webp))(\?.*)?$/i, `_${width}x${width}$1$2`);
  }

  function normalizeProduct(product) {
    const tags = product.tags || [];
    const artistTag = tags.find((t) => t.startsWith(ARTIST_TAG_PREFIX)) || '';
    const collectionTags = tags
      .filter((t) => t.startsWith(COLLECTION_TAG_PREFIX))
      .map((t) => t.slice(COLLECTION_TAG_PREFIX.length));
    const featured = product.images && product.images[0] && product.images[0].src;
    return {
      key: product.handle.replace(/-art-print$/, ''),
      shopify_id: product.id,
      handle: product.handle,
      artwork_handle: product.handle.replace(/-art-print$/, ''),
      artist_handle: artistTag.slice(ARTIST_TAG_PREFIX.length),
      title: (product.title || '').replace(/ - Art Print$/i, ''),
      artist_name: artistTag.slice(ARTIST_TAG_PREFIX.length).replace(/-/g, ' '),
      collections: collectionTags,
      orientation: 'portrait',
      image: shopifyImageUrl(featured, 400),
      image_full: featured,
    };
  }

  async function fetchArtworks({ collection, sourceCollection, page = 1, query = '', artist = '', orient = '', sort = '' }) {
    const idx = await visibleIndex();
    if (idx) {
      // Shared pipeline (assets/adg-filters.js), the same one the design
      // grids use — so a filter means the same thing on both.
      //
      // BUG THIS FIXES: `collection` was accepted and then never applied on
      // the index path. Picking "63 American National Parks" in the builder
      // returned the first 60 rows of the WHOLE index — Zoo: Zebras, Zoo:
      // Pandas, Zoo: Maccaws — so the entire collection-first flow was
      // cosmetic. Filter first, THEN slice, or pagination pages through the
      // wrong set.
      const list = window.ADGFilters
        ? window.ADGFilters.filterAndSort(idx, { collection, q: query, artist, orient, sort })
        : idx;
      // The WHOLE filtered set. Slicing it here was the builder imitating a
      // server feed over data already in memory; adg-grid-core pages it now,
      // the same way the design grids always have.
      return { artworks: list };
    }
    // ---- legacy fallback: the old product-collection feed ----
    // The shopper's chosen collection wins; the section's sourceCollection
    // ("art-prints") is only the seed for the pre-choice state. The old
    // order ignored the choice entirely, so on fallback every tile showed
    // the same art-prints feed.
    const url = `/collections/${encodeURIComponent(collection || sourceCollection)}/products.json?limit=${PAGE_SIZE}&page=${page}`;
    const res = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error(`Storefront returned ${res.status}`);
    const json = await res.json();
    const raw = json.products || [];
    let filtered = raw.filter((p) => (p.tags || []).includes(ART_PRINT_TAG));
    if (query && query.trim()) {
      const q = query.toLowerCase();
      filtered = filtered.filter((p) =>
        (p.title || '').toLowerCase().includes(q) ||
        (p.tags || []).some((t) => t.toLowerCase().includes(q))
      );
    }
    return {
      artworks: filtered.map(normalizeProduct),
      hasMore: raw.length === PAGE_SIZE,
      nextPage: page + 1,
    };
  }

  // Identity helper: index picks carry .key; picks restored from older
  // localStorage sessions may only have artwork_handle / shopify_id.
  function pickKey(p) {
    return (p && (p.key || p.artwork_handle || String(p.shopify_id))) || '';
  }

  /* A pick restored from localStorage is a snapshot of whatever the index
     said the day it was saved — it has no `cdn`, so it cannot ask for a
     right-sized or WebP image, and if an artwork's URL ever moves it would
     keep pointing at the old one. Re-resolve it against the index that is
     actually loaded, keyed by handle. Falls back to the stored object
     untouched when the index has not loaded or no longer carries it, so a
     restored set always renders exactly as it did before. */
  let _byKey = null;
  function live(p) {
    if (!p || p.cdn || !_index) return p;
    if (!_byKey) {
      _byKey = {};
      _index.forEach(function (a) { _byKey[pickKey(a)] = a; });
    }
    const hit = _byKey[pickKey(p)];
    return hit ? Object.assign({}, p, { cdn: hit.cdn, image: hit.image }) : p;
  }

  global.BYO = global.BYO || {};
  // Exposed so the filter bar can build its artist list from the artworks
  // this builder can actually show (visibleIndex applies the portrait-only
  // rule for calendars), rather than from the raw index.
  async function allArtworks() { return await visibleIndex(); }

  global.BYO.data = { fetchArtworks, configure, allArtworks };

  // -------- Picker UI --------

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.from((root || document).querySelectorAll(sel)); }

  function debounce(fn, ms) {
    let t;
    return function () {
      clearTimeout(t);
      const args = arguments;
      t = setTimeout(() => fn.apply(this, args), ms);
    };
  }

  // Month abbreviations for calendar slot labels (matches Heather's Figma).
  // For non-calendar kinds (notecards, postcards), fall back to numbers.
  const MONTH_ABBR = ['Jan','Feb','Mar','April','May','June','July','Aug','Sept','Oct','Nov','Dec'];
  function slotDisplayLabel(label, kind) {
    if (label === 'cover') return 'Cover';
    if (kind === 'calendar') {
      const idx = Number(label) - 1;
      if (idx >= 0 && idx < MONTH_ABBR.length) return MONTH_ABBR[idx];
    }
    return String(label);
  }

  function init(root) {

    const required = Number(root.dataset.picksRequired) || 12;
    const hasCover = root.dataset.hasCover === 'true';
    const collection = root.dataset.sourceCollection || 'art-prints';
    const kind = root.dataset.kind || 'calendar';
    const productId = root.dataset.productId;

    // Point the data adapter at the artwork index. Only calendars exclude
    // landscape (notecards/postcards print both orientations).
    BYO.data.configure({
      indexUrl: root.dataset.indexUrl || '',
      // Fail closed, same as the liquid: only the card builders opt INTO
      // landscape. A missing data-attribute (stale cached section) must not be
      // what lets a landscape design into a calendar.
      orientation: root.dataset.allowedOrientation
        || (kind === 'notecards' || kind === 'postcards' ? 'all' : 'portrait'),
    });

    const STORAGE_KEY = `byo:${root.dataset.kind || 'calendar'}:${productId}`;
    // Add-to-cart navigates away to /cart, so "this set is already in the cart"
    // has to survive the round trip. It drives the Clear button's wording.
    const ADDED_KEY = `${STORAGE_KEY}:added`;
    const ADDED_SIG_KEY = `${STORAGE_KEY}:added-sig`;

    const state = {
      picks: new Array(required).fill(null), // [{ artwork data }, ...]
      cover: hasCover ? null : undefined,
      artworks: [],
      loading: false,
      firstLoad: true,          // only a first load may claim a scroll return
      query: '',
      collectionFilter: 'all',  // handle from collection_filter_options
      addedToCart: false,       // this set has been added at least once
    };

    // Restore from localStorage if present
    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
      if (stored && Array.isArray(stored.picks) && stored.picks.length === required) {
        state.picks = stored.picks;
        if (hasCover) state.cover = stored.cover || null;
      }
    } catch (e) { /* ignore */ }
    try { state.addedToCart = localStorage.getItem(ADDED_KEY) === '1'; } catch (e) { /* ignore */ }
    try { state.addedSig = localStorage.getItem(ADDED_SIG_KEY) || ''; } catch (e) { /* ignore */ }

    /* What exactly went to the cart. Cover first, then picks in order —
       so replacing a design, clearing one, or reordering a calendar all
       read as "a different set", which is what flips the button back from
       "Add Another To Cart" to "Add To Cart" (Justin's flow, 08-05). */
    function setSignature() {
      const parts = state.picks.map((p) => (p ? pickKey(p) : ''));
      if (hasCover) parts.unshift(state.cover ? pickKey(state.cover) : '');
      return parts.join('|');
    }

    function markAddedToCart() {
      state.addedToCart = true;
      state.addedSig = setSignature();
      try {
        localStorage.setItem(ADDED_KEY, '1');
        localStorage.setItem(ADDED_SIG_KEY, state.addedSig);
      } catch (e) { /* ignore */ }
    }

    function persist() {
      // An empty set is not worth storing — and after Clear, leaving a husk
      // behind would mean "clear" hadn't really cleared anything.
      const anyPicked = state.picks.some(Boolean) || !!state.cover;
      if (!anyPicked) { clearPersisted(); return; }
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
          picks: state.picks,
          cover: state.cover || null,
          savedAt: Date.now(),
        }));
      } catch (e) { /* ignore quota errors */ }
    }
    function clearPersisted() {
      try {
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(ADDED_KEY);
        localStorage.removeItem(ADDED_SIG_KEY);
      } catch (e) { /* ignore */ }
    }

    // GLOBAL curation, from Theme settings via the root's data attributes —
    // the same picks/blocked/demote the design pages honour (Justin, 08-08:
    // the builders ignoring Joel's picks was the same-view inconsistency).
    const CUR_SUFFIX_RE = /-(art-print|canvas|metal-sign|notecard-set|postcard-set|puzzle)$/;
    const curHandle = (h) => String(h).replace(CUR_SUFFIX_RE, '');
    const pickOrder = (root.dataset.picks || '').split('|').filter(Boolean).map(curHandle);
    const blockedSet = new Set((root.dataset.blocked || '').split('|').filter(Boolean).map(curHandle));
    const demoteTerms = (root.dataset.demoteTitles || '')
      .split(',').map((s) => s.trim().toLowerCase()).filter(Boolean);
    const isDemoted = (t) => {
      const s = String(t || '').toLowerCase();
      return demoteTerms.some((term) => s.indexOf(term) !== -1);
    };
    const NEW_DAYS = parseInt(root.dataset.newDays, 10) || 0;
    const newCutoff = NEW_DAYS > 0
      ? new Date(Date.now() - NEW_DAYS * 86400000).toISOString().slice(0, 10)
      : '';
    // Same rule as the design grids: the TRUE original date (ADGFilters.
    // pubDate), never the store publish date alone. Guarded because the
    // collection-feed fallback runs even if adg-filters failed to load.
    const isNewArt = (a) => !!(newCutoff && window.ADGFilters
      && window.ADGFilters.pubDate(a) >= newCutoff);

    const els = {
      tray: $('[data-byo-tray]', root),
      slots: $('[data-byo-slots]', root),
      count: $('[data-byo-count]', root),
      clear: $('[data-byo-clear]', root),
      grid: $('[data-byo-grid]', root),
      // The filter row is the SHARED snippet (adg-filter-row.liquid), so the
      // hooks are the design grids' data-df-* vocabulary — one component,
      // one set of attribute names, on every grid.
      search: $('[data-df-q]', root),
      searchWrap: $('[data-df-searchwrap]', root),
      fArtist: $('[data-df-artist]', root),
      fArtistWrap: $('[data-df-artistwrap]', root),
      fOrient: $('[data-df-orient]', root),
      fOrientWrap: $('[data-df-orientwrap]', root),
      fSort: $('[data-df-sort]', root),
      fSortWrap: $('[data-df-sortwrap]', root),
      chips: $('[data-df-chips]', root),
      dfCount: $('[data-df-count]', root),
      collectionWrap: $('[data-byo-collection-wrap]', root),
      collectionToggle: $('[data-byo-collection-toggle]', root),
      collectionLabel: $('[data-byo-collection-label]', root),
      collectionMenu: $('[data-byo-collection-menu]', root),
      loading: $('[data-byo-loading]', root),
      empty: $('[data-byo-empty-msg]', root),
      loadmore: $('[data-byo-loadmore]', root),
      sentinel: $('[data-byo-sentinel]', root),
      addBtn: $('[data-byo-add]', root),
      addLabel: $('[data-byo-add-label]', root),
      addHint: $('[data-byo-add-hint]', root),
      variant: $('[data-byo-variant]', root),
      price: $('[data-byo-price]', root),
      previewBtn: $('[data-byo-preview]', root),
      previewModal: $('[data-byo-preview-modal]', root),
      previewCover: $('[data-byo-preview-cover]', root),
      previewGrid: $('[data-byo-preview-grid]', root),
      previewAdd: $('[data-byo-preview-add]', root),
      toggleBtn: $('[data-byo-toggle]', root),
      submitBar: $('[data-byo-submit-bar]', root),
    };

    // The shared grid controller (assets/adg-grid-core.js). Cards are still
    // built here — a builder card PICKS rather than navigates, which is a
    // real difference — but everything around them is the design grids' code.
    const gridCore = global.ADGGridCore.create({
      grid: els.grid,
      sentinel: els.sentinel,
      moreBtn: els.loadmore,
      firstBatch: PAGE_SIZE,
      pageSize: PAGE_SIZE,
      lookahead: 900,
      // Endless, the same as the design grids' default "Load more behaviour".
      // The core's eight-batch budget is for surfaces that opt into handing
      // over to a button; picking twelve designs out of three thousand is not
      // one of them, and a button appearing mid-collection here is the exact
      // thing that has been reported.
      autoBatches: Infinity,
      maxItems: Infinity,
      renderCard: (art) => {
        const pickedIds = new Set(state.picks.filter(Boolean).map(pickKey));
        if (state.cover) pickedIds.add(pickKey(state.cover));
        return buildCard(art, pickedIds.has(pickKey(art)));
      },
      onRender: () => refreshCardStates()
    });


    // ----------- Rendering -----------

    function renderTray() {
      // Cover slot
      if (hasCover) {
        const coverSlot = els.slots.querySelector('[data-byo-slot="cover"]');
        renderSlot(coverSlot, state.cover, 'cover');
      }
      // Numbered slots
      for (let i = 0; i < required; i++) {
        const slot = els.slots.querySelector(`[data-byo-slot="${i + 1}"]`);
        renderSlot(slot, state.picks[i], i + 1);
      }

      // Mark the first empty slot as the active "next" target (dashed border,
      // per Heather). Every other empty slot stays solid grey.
      $$('.byo-slot--next', els.slots).forEach((s) => s.classList.remove('byo-slot--next'));
      const nextEmpty = els.slots.querySelector('.byo-slot[data-byo-empty]');
      if (nextEmpty) nextEmpty.classList.add('byo-slot--next');

      const filledCount = state.picks.filter(Boolean).length;
      els.count.textContent = String(filledCount);

      const allFilled = filledCount === required && (!hasCover || state.cover);
      els.addBtn.disabled = !allFilled;
      // Title Case per Heather's Figma button copy
      const remaining = required - filledCount;
      // The button always reads "Add To Cart" — what's outstanding goes in the
      // hint line under it. Before, the progress text WAS the label, so the
      // word Cart never appeared until the last pick and Joel reported the
      // button as missing. See byo-cart-form.liquid.
      const hint = allFilled
        ? ''
        : hasCover && !state.cover && filledCount === required
          ? 'Pick A Cover First'
          : `Pick ${remaining} More Design${remaining === 1 ? '' : 's'}`;
      // "Add Another To Cart" when THIS EXACT set already went to the cart
      // and hasn't been touched since — truthful, because re-adding the same
      // configuration merges as quantity 2. The moment any pick changes, the
      // signature differs and the button reads plain "Add To Cart" again.
      const unchangedSinceCart = allFilled && state.addedToCart &&
        state.addedSig && setSignature() === state.addedSig;
      const addWords = unchangedSinceCart ? 'Add Another To Cart' : 'Add To Cart';
      els.addLabel.textContent = addWords;
      if (els.addHint) {
        els.addHint.textContent = hint;
        els.addHint.hidden = !hint;
      }
      // Screen readers get the whole story from the button, not the visual stack.
      els.addBtn.setAttribute('aria-label', hint ? `${addWords}. ${hint} first.` : addWords);

      // Mirror state into the preview-modal Add-to-Cart button if present
      if (els.previewAdd) {
        els.previewAdd.disabled = !allFilled;
        els.previewAdd.textContent = allFilled ? addWords : `Add To Cart — ${hint}`;
      }

      els.clear.hidden = filledCount === 0 && !state.cover;
      // Before anything is in the cart this button only clears the set, so it
      // says so. Once a set HAS been added, picks deliberately survive (Jul 16
      // meeting) and the same button becomes the way to start the next one.
      els.clear.textContent = state.addedToCart ? 'Build Another' : 'Clear';
      els.clear.setAttribute('aria-label', state.addedToCart
        ? 'Clear these picks and build another'
        : 'Clear all picks');
      persist();
    }

    function renderSlot(slot, item, label) {
      if (!slot) return;
      slot.innerHTML = '';
      slot.draggable = (kind === 'calendar') && !!item;   // reorder only matters for the calendar; off for notecards/postcards (Jun 18 demo)
      slot.dataset.label = String(label);
      if (item) {
        slot.removeAttribute('data-byo-empty');
        slot.classList.add('byo-slot--filled');
        const img = document.createElement('img');
        // crossOrigin must be set BEFORE src so the request is made with
        // CORS. Shopify CDN sends Access-Control-Allow-Origin:*, so this
        // succeeds and lets canvas.drawImage(img) produce an untainted
        // canvas — needed for setDragImage() in older Safari.
        img.crossOrigin = 'anonymous';
        img.decoding = 'async';
        img.loading = 'lazy';
        // A tray thumbnail paints at ~59px. It was pulling the 512 JPEG at
        // 46.7 KB; the smallest rung in WebP is 11.7 KB and still 4x the
        // pixels it needs. live() re-resolves picks restored from an older
        // session, which carry no `cdn`, against the loaded index.
        const shown = live(item);
        img.src = shown.cdn ? shown.cdn + '/256.webp' : shown.image;
        img.alt = item.title;
        img.className = 'byo-slot__img';
        img.draggable = false;
        img.setAttribute('draggable', 'false');
        slot.appendChild(img);
        // Desktop hover-X — hidden on mobile via CSS; touch users tap to remove
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'byo-slot__remove';
        btn.setAttribute('aria-label', `Remove ${item.title}`);
        btn.textContent = '×';
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          removeFromSlot(label);
        });
        slot.appendChild(btn);
      } else {
        slot.setAttribute('data-byo-empty', '');
        slot.classList.remove('byo-slot--filled');
        const span = document.createElement('span');
        span.className = 'byo-slot__label';
        // Month names for calendar kind, numbers for others. Empty slots
        // get a label so customers know what month/position they're filling.
        span.textContent = slotDisplayLabel(label, kind);
        slot.appendChild(span);
      }
    }

    // Touch-device detection: prefer feature detection over media query.
    // matchMedia('(hover: hover)') lies in DevTools mobile emulation.
    const IS_TOUCH = ('ontouchstart' in window) || navigator.maxTouchPoints > 0;

    // Tracks whether a BYO drag is in progress. Set true on any of our
    // dragstart handlers, false on dragend/drop. The edge-auto-scroll
    // RAF loop uses this; no need to read dataTransfer.types (which
    // Firefox blocks during dragover anyway).
    let byoDragActive = false;

    // Edge auto-scroll plumbing — used by both card and slot dragstart.
    // Real implementation is set inside setupSlotDragAndDrop(); these
    // are stubbed here so calling them before setup is harmless.
    let startDragLoop = function () {};
    let stopDragLoop = function () {};

    function setupSlotDragAndDrop() {
      // Edge auto-scroll for the picks strip during drag.
      // ---------------------------------------------------------------
      // Architecture: instead of triggering scroll from the dragover
      // event (which may not fire while the finger holds still at an
      // edge, or may be missed if a deeper handler stopPropagation's),
      // we run a requestAnimationFrame loop the whole time a BYO drag is
      // active. Every drag-related event in the CAPTURE phase just
      // updates lastDragX/Y. The RAF loop reads those and decides
      // whether to scroll. This guarantees scrolling even if the user
      // holds the finger still at the edge.
      const EDGE_ZONE = 70;          // px from edge that triggers scroll
      // Speed ramps up the closer you are to the edge: at the very
      // edge (0px in) it's MAX_SPEED, at EDGE_ZONE it's near zero.
      // Gives a "slow at first, faster the further you push" feel.
      const MAX_SPEED = 12;          // px per frame at the very edge
      const MIN_SPEED = 2;           // px per frame at EDGE_ZONE threshold
      let lastDragX = 0, lastDragY = 0, dragLoopRAF = null;

      function speedFor(distFromEdge) {
        // distFromEdge: 0 = at edge, EDGE_ZONE = at outer threshold
        // Clamp to [0, EDGE_ZONE].
        const clamped = Math.max(0, Math.min(EDGE_ZONE, distFromEdge));
        const t = 1 - (clamped / EDGE_ZONE); // 1 at edge, 0 at outer
        return MIN_SPEED + (MAX_SPEED - MIN_SPEED) * t;
      }

      function withinStripBand(clientY, rect) {
        // Generous tolerance so the user can pull the cursor slightly
        // outside the strip without losing the scroll.
        const tol = 80;
        return clientY >= rect.top - tol && clientY <= rect.bottom + tol;
      }

      function tick() {
        if (!byoDragActive) { dragLoopRAF = null; return; }
        const collapsed = els.tray.classList.contains('byo-tray--collapsed');
        const rect = els.slots.getBoundingClientRect();
        if (collapsed) {
          // Horizontal scroll for the collapsed strip.
          const overflowsX = els.slots.scrollWidth > els.slots.clientWidth + 1;
          if (overflowsX && withinStripBand(lastDragY, rect)) {
            const leftDist = lastDragX - rect.left;
            const rightDist = rect.right - lastDragX;
            if (leftDist < EDGE_ZONE) {
              els.slots.scrollLeft -= speedFor(leftDist);
            } else if (rightDist < EDGE_ZONE) {
              els.slots.scrollLeft += speedFor(rightDist);
            }
          }
        } else {
          // Vertical scroll for the expanded grid (when it overflows the
          // 50vh cap). Same edge-zone + ramp logic, just on the Y axis.
          const overflowsY = els.slots.scrollHeight > els.slots.clientHeight + 1;
          // Loose X bounds so user can be near the grid's edge column.
          const withinX = lastDragX >= rect.left - 20 && lastDragX <= rect.right + 20;
          if (overflowsY && withinX) {
            const topDist = lastDragY - rect.top;
            const botDist = rect.bottom - lastDragY;
            if (topDist < EDGE_ZONE) {
              els.slots.scrollTop -= speedFor(topDist);
            } else if (botDist < EDGE_ZONE) {
              els.slots.scrollTop += speedFor(botDist);
            }
          }
        }
        dragLoopRAF = requestAnimationFrame(tick);
      }

      // Assign to the outer-scope vars so card+slot dragstart handlers
      // can call them.
      startDragLoop = function () {
        if (dragLoopRAF !== null) return;
        // CRITICAL: temporarily disable scroll-snap on the strip.
        // The collapsed mobile strip uses `scroll-snap-type: x mandatory`
        // for nice manual swipe snapping. But mandatory snap rejects any
        // programmatic scrollLeft change that doesn't land exactly on a
        // snap point — our small per-frame increments (+2 to +12px) get
        // snapped back to the current slot every frame, so the strip
        // visually never moves. Switch to 'none' for the duration of the
        // drag; restore on dragend. (Sortable.js, dnd-kit, react-dnd all
        // do this same workaround for the same reason.)
        els.slots.style.scrollSnapType = 'none';
        dragLoopRAF = requestAnimationFrame(tick);
      };
      stopDragLoop = function () {
        if (dragLoopRAF !== null) {
          cancelAnimationFrame(dragLoopRAF);
          dragLoopRAF = null;
        }
        // Restore inline override; CSS rule (`x mandatory`) takes over again.
        els.slots.style.scrollSnapType = '';
      };

      // Capture-phase listeners catch the event before any descendant
      // can stopPropagation or preventDefault away the position info.
      function captureDragXY(e) {
        if (typeof e.clientX === 'number' && (e.clientX !== 0 || e.clientY !== 0)) {
          lastDragX = e.clientX;
          lastDragY = e.clientY;
        }
      }
      document.addEventListener('dragover', captureDragXY, true);
      document.addEventListener('drag', captureDragXY, true);
      document.addEventListener('dragenter', captureDragXY, true);

      document.addEventListener('dragend', () => {
        stopDragLoop();
        byoDragActive = false;
      });
      document.addEventListener('drop', () => {
        stopDragLoop();
        byoDragActive = false;
      });


      els.slots.addEventListener('dragstart', (e) => {
        const slot = e.target.closest('.byo-slot--filled');
        if (!slot) return;
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('application/x-byo-slot', slot.dataset.label);
        slot.classList.add('byo-slot--dragging');
        byoDragActive = true;
        startDragLoop();
        const img = slot.querySelector('img');
        setSmallDragImage(e, img); // pass the loaded <img>, not its src
      });
      els.slots.addEventListener('dragend', () => {
        $$('.byo-slot--dragging', els.slots).forEach((s) => s.classList.remove('byo-slot--dragging'));
        $$('.byo-slot--drop-over', els.slots).forEach((s) => s.classList.remove('byo-slot--drop-over'));
        stopDragLoop();
        byoDragActive = false;
      });
      els.slots.addEventListener('dragover', (e) => {
        e.preventDefault();
        const slot = e.target.closest('.byo-slot');
        if (!slot) return;
        e.dataTransfer.dropEffect = e.dataTransfer.types.includes('application/x-byo-slot') ? 'move' : 'copy';
        slot.classList.add('byo-slot--drop-over');
      });
      els.slots.addEventListener('dragleave', (e) => {
        const slot = e.target.closest('.byo-slot');
        if (slot) slot.classList.remove('byo-slot--drop-over');
      });
      els.slots.addEventListener('drop', (e) => {
        const targetSlot = e.target.closest('.byo-slot');
        stopDragLoop();
        if (!targetSlot) return;
        e.preventDefault();
        targetSlot.classList.remove('byo-slot--drop-over');
        const targetLabel = targetSlot.dataset.label;
        const slotData = e.dataTransfer.getData('application/x-byo-slot');
        const cardData = e.dataTransfer.getData('application/x-byo-card');
        if (slotData) {
          swapSlots(slotData, targetLabel);
        } else if (cardData) {
          const art = state.artworks.find((a) => pickKey(a) === cardData);
          if (art) placeInSlot(art, targetLabel);
        }
      });

      // --- Block iOS image-callout / browser context menu during touch ---
      // Without this, a 500-700ms long-press on a filled slot fires the
      // native "Save image / Copy" sheet BEFORE the polyfill's 400ms
      // press-hold drag can engage — making touch drag impossible.
      // The polyfill itself only fires its synthetic contextmenu after
      // 900ms (and only if drag hasn't started), so blocking the native
      // one is safe.
      root.addEventListener('contextmenu', (e) => {
        if (e.target.closest('.byo-slot') || e.target.closest('.adg-card--pickable')) {
          e.preventDefault();
        }
      });

      // --- Tap to REVEAL the × button on filled slots (touch devices only) ---
      // Quick tap (no movement, < 280ms) on a filled slot adds the
      // .byo-slot--show-remove class which makes the × visible (CSS).
      // The × itself has its own click handler that removes the pick.
      // Press-hold (>= polyfill's pressHoldDelayMS=400ms) starts drag instead.
      // Movement aborts the tap so the strip/page scrolls normally.
      // Tapping elsewhere hides any revealed ×.
      if (IS_TOUCH) {
        let tapStartX = 0, tapStartY = 0, tapStartT = 0, tapSlot = null, tapMoved = false;
        let revealTimer = null;
        const TAP_MOVE_THRESHOLD = 10; // px — anything more counts as scroll
        const TAP_MAX_DURATION = 280;  // ms — below pressHoldDelayMS so drag still wins
        const REVEAL_AUTO_HIDE_MS = 3000;

        function hideAllRemove() {
          $$('.byo-slot--show-remove', els.slots).forEach((s) =>
            s.classList.remove('byo-slot--show-remove'));
          if (revealTimer) { clearTimeout(revealTimer); revealTimer = null; }
        }
        function revealRemove(slot) {
          // Hide any previously revealed × first so only one is visible
          hideAllRemove();
          slot.classList.add('byo-slot--show-remove');
          revealTimer = setTimeout(() => {
            slot.classList.remove('byo-slot--show-remove');
            revealTimer = null;
          }, REVEAL_AUTO_HIDE_MS);
        }

        els.slots.addEventListener('touchstart', (e) => {
          const slot = e.target.closest('.byo-slot--filled');
          if (!slot) { tapSlot = null; return; }
          // Tap on the × itself — let its click handler do the removal,
          // don't treat as a slot tap.
          if (e.target.closest('.byo-slot__remove')) { tapSlot = null; return; }
          const t = e.touches[0];
          tapStartX = t.clientX;
          tapStartY = t.clientY;
          tapStartT = Date.now();
          tapSlot = slot;
          tapMoved = false;
        }, { passive: true });

        els.slots.addEventListener('touchmove', (e) => {
          if (!tapSlot) return;
          const t = e.touches[0];
          if (Math.abs(t.clientX - tapStartX) > TAP_MOVE_THRESHOLD ||
              Math.abs(t.clientY - tapStartY) > TAP_MOVE_THRESHOLD) {
            tapMoved = true;
          }
        }, { passive: true });

        els.slots.addEventListener('touchend', () => {
          if (!tapSlot || tapMoved) { tapSlot = null; return; }
          const dt = Date.now() - tapStartT;
          if (dt > TAP_MAX_DURATION) { tapSlot = null; return; }
          // Clean quick tap — reveal × so the user can tap it to remove.
          revealRemove(tapSlot);
          tapSlot = null;
          // Do NOT preventDefault: let the synthetic click fall through so
          // the user can tap the × in the same gesture if they want.
        });

        els.slots.addEventListener('touchcancel', () => { tapSlot = null; });

        // Tap anywhere outside a filled slot hides revealed × buttons.
        document.addEventListener('touchstart', (e) => {
          if (!e.target.closest('.byo-slot--filled')) hideAllRemove();
        }, { passive: true });
      }
    }

    function pickFromLabel(label) {
      if (label === 'cover') return state.cover;
      return state.picks[Number(label) - 1] || null;
    }
    function setSlot(label, item) {
      if (label === 'cover') state.cover = item;
      else state.picks[Number(label) - 1] = item;
    }
    function swapSlots(fromLabel, toLabel) {
      if (fromLabel === toLabel) return;
      const a = pickFromLabel(fromLabel);
      const b = pickFromLabel(toLabel);
      setSlot(fromLabel, b);
      setSlot(toLabel, a);
      renderTray();
      refreshCardStates();
    }
    function placeInSlot(art, label) {
      // Duplicates allowed: dropping a card onto a slot places a copy there
      // (replacing whatever occupied that slot), even if the same artwork is
      // already used in another slot. Slot-to-slot REORDERING still swaps —
      // that's handled separately in the drop handler via swapSlots().
      setSlot(label, art);
      renderTray();
      refreshCardStates();
    }

    /* The list the shopper should see, filtered ONCE. The Collection
       dropdown used to filter again at render time, after paging had already
       decided what to draw — so a page whose rows the second filter removed
       appended nothing, which the scroll engine reads as a stall. Filtering
       here, before the core pages it, means what is counted and what is drawn
       are the same thing. */
    function visibleList() {
      const h = state.collectionFilter || 'all';
      return (h === 'all')
        ? state.artworks
        : state.artworks.filter((a) => (a.collections || []).indexOf(h) !== -1);
    }

    function renderGrid() {
      const list = visibleList();
      els.empty.hidden = list.length > 0;
      gridCore.setItems(list, state.firstLoad);
      state.firstLoad = false;
      refreshCardStates();
      // Same sentence the design grids print (adg-artgrid updateCount):
      // the filtered count against the collection's total.
      if (els.dfCount) {
        const n = list.length;
        const total = state.scopeCount || n;
        const word = n === 1 ? 'design' : 'designs';
        els.dfCount.textContent = (n >= total) ? n + ' ' + word : n + ' of ' + total + ' ' + word;
      }
    }

    function buildCard(art, picked) {
      // Built by the shared factory (assets/adg-card.js) — same DOM and same
      // behaviour as the design grid, so a card fix lands once instead of
      // twice. This is step 2a: structure unified, SKIN unchanged, so the
      // builder looks exactly as it did. Step 2b switches the skin to
      // .adg-card so the two surfaces finally match visually.
      //
      // crossorigin is not optional here: the live-preview dock draws these
      // images into a canvas, and without it the canvas taints and the
      // preview silently stops working.
      const expandButton = document.createElement('button');
      expandButton.type = 'button';
      expandButton.className = 'byo-card__expand';
      expandButton.setAttribute('data-byo-expand', '');
      expandButton.setAttribute('aria-label', `Preview ${art.title} larger`);
      expandButton.innerHTML = `
            <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
              <path fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"
                d="M9 2h5v5M14 2L8 8M7 14H2V9M2 14l6-6"/>
            </svg>`;

      const card = window.ADGCard.create(
        { id: pickKey(art), title: art.title, artist: art.artist_name, img: art.image, isNew: isNewArt(art) },
        {
          // Step 2b: the adg skin, so the builder and the design grids are
          // the same card. Picking is a modifier, not a separate component.
          mode: 'button',
          picked: picked,
          className: 'adg-card--pickable' + (picked ? ' is-picked' : ''),
          crossorigin: true,
          decoding: true,
          badge: '\u2713',
          expand: 'inside',
          expandButton: expandButton
        }
      );
      card.setAttribute('aria-label', `${art.title}${art.artist_name ? ' by ' + art.artist_name : ''}`);

      const expandBtn = card.querySelector('[data-byo-expand]');
      if (expandBtn) {
        const openLightbox = (e) => {
          e.stopPropagation();
          e.preventDefault();
          openArtworkLightbox(art);
        };
        expandBtn.addEventListener('click', openLightbox);
        expandBtn.addEventListener('pointerup', (e) => e.stopPropagation());
        expandBtn.addEventListener('pointerdown', (e) => e.stopPropagation());
      }
      // Pointer-based tap detection. Click events conflict with draggable=true
      let downX = 0, downY = 0, downTime = 0;
      card.addEventListener('pointerdown', (e) => {
        downX = e.clientX; downY = e.clientY; downTime = Date.now();
      });
      card.addEventListener('pointerup', (e) => {
        const dx = Math.abs(e.clientX - downX);
        const dy = Math.abs(e.clientY - downY);
        const dt = Date.now() - downTime;
        if (dx < 6 && dy < 6 && dt < 500) {
          handleCardClick(art);
        }
      });
      card.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleCardClick(art);
        }
      });
      // Desktop drag (HTML5 native); touch drag goes through the polyfill
      card.draggable = true;
      card.addEventListener('dragstart', (e) => {
        e.dataTransfer.effectAllowed = 'copy';
        e.dataTransfer.setData('application/x-byo-card', pickKey(art));
        card.classList.add('is-dragging');
        byoDragActive = true;
        startDragLoop();
        // Pass the already-loaded <img> element so the ghost shows the
        // artwork, not a white box.
        const cardImg = card.querySelector('img');
        setSmallDragImage(e, cardImg);
      });
      card.addEventListener('dragend', () => {
        card.classList.remove('is-dragging');
        stopDragLoop();
        byoDragActive = false;
      });
      // (DragDropTouch polyfill handles touch drag via HTML5 events — no custom touch code needed)
      return card;
    }

    function escapeHtml(s) {
      return String(s || '').replace(/[&<>"']/g, (c) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
      }[c]));
    }

    // Build a small (~80px) drag preview from the loaded <img>.
    //
    // Why a <canvas> instead of cloning the <img>: setDragImage() snapshots
    // the target element SYNCHRONOUSLY at the moment dragstart fires. A
    // freshly cloned <img> hasn't laid out yet — even if the src is in the
    // browser cache, the new node has to attach + decode + paint before it
    // renders pixels. The snapshot catches a blank img → white box.
    //
    // drawImage() with a fully-loaded HTMLImageElement is synchronous: the
    // canvas has real pixels the moment we return from drawImage. So when
    // we pass the canvas to setDragImage(), the snapshot is the actual art.
    //
    // Caveat: drawImage on a cross-origin image without CORS taints the
    // canvas. setDragImage on a tainted canvas still works visually in
    // Chrome/Safari/Firefox — the taint only blocks pixel readback
    // (getImageData/toBlob), not display.
    const GHOST_SIZE = 80;
    function setSmallDragImage(e, srcImgEl) {
      if (!e.dataTransfer || typeof e.dataTransfer.setDragImage !== 'function') return;
      // Need a real, fully-decoded image to draw.
      if (!srcImgEl || !srcImgEl.complete || !srcImgEl.naturalWidth) return;

      try {
        const dpr = Math.min(window.devicePixelRatio || 1, 3); // cap at 3x — bigger wastes memory
        const canvas = document.createElement('canvas');
        // High-DPI: render at devicePixelRatio for crisp ghosts on retina.
        canvas.width = GHOST_SIZE * dpr;
        canvas.height = GHOST_SIZE * dpr;
        canvas.style.cssText = [
          'position:fixed',
          'top:-1000px',
          'left:-1000px',
          'width:' + GHOST_SIZE + 'px',
          'height:' + GHOST_SIZE + 'px',
          'border-radius:6px',
          'box-shadow:0 6px 18px rgba(0,0,0,0.35)',
          'pointer-events:none',
          'z-index:99999',
        ].join(';');

        const ctx = canvas.getContext('2d');
        // White backing so transparent or letterboxed images don't look
        // like a black square on dark mode.
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // object-fit: contain — fit the whole image, preserve aspect ratio.
        const padding = 4 * dpr;
        const targetW = canvas.width - padding * 2;
        const targetH = canvas.height - padding * 2;
        const iw = srcImgEl.naturalWidth;
        const ih = srcImgEl.naturalHeight;
        const scale = Math.min(targetW / iw, targetH / ih);
        const dw = iw * scale;
        const dh = ih * scale;
        const dx = (canvas.width - dw) / 2;
        const dy = (canvas.height - dh) / 2;
        ctx.drawImage(srcImgEl, dx, dy, dw, dh);

        document.body.appendChild(canvas);
        e.dataTransfer.setDragImage(canvas, GHOST_SIZE / 2, GHOST_SIZE / 2);
        // Remove on next tick — the browser has already captured the image.
        setTimeout(() => canvas.remove(), 0);
      } catch (err) {
        // drawImage may throw on some pathological src values; the browser
        // will fall back to its default drag image, which is acceptable.
      }
    }

    // How many times each artwork is used across cover + picks. Duplicates are
    // allowed, so this drives both the picked outline AND a "×N" count badge.
    function pickCounts() {
      const counts = new Map();
      const bump = (p) => { if (p) counts.set(pickKey(p), (counts.get(pickKey(p)) || 0) + 1); };
      state.picks.forEach(bump);
      if (state.cover) bump(state.cover);
      return counts;
    }

    function refreshCardStates() {
      const counts = pickCounts();
      $$('.adg-card--pickable', els.grid).forEach((card) => {
        const n = counts.get(card.dataset.id) || 0;
        card.classList.toggle('is-picked', n > 0);
        card.setAttribute('aria-pressed', n > 0 ? 'true' : 'false');
        const badge = card.querySelector('.adg-card__badge');
        if (badge) badge.textContent = n > 1 ? ('×' + n) : '✓';
      });
    }

    // ----------- Pick logic -----------

    function handleCardClick(art) {
      // Tapping a card ADDS it to the next open spot — duplicates included, so a
      // customer can use the same design more than once (e.g. two of the same
      // notecard). Removal is via the × on the pick in the tray, not by tapping
      // the card again. Fill the cover first if empty, otherwise next empty slot.
      if (hasCover && !state.cover) {
        state.cover = art;
      } else {
        const emptyIdx = state.picks.findIndex((p) => p === null);
        if (emptyIdx < 0) return; // tray full
        state.picks[emptyIdx] = art;
      }
      renderTray();
      refreshCardStates();
      // Auto-advance the picks strip so the next empty slot is visible.
      // Only triggers on tap-to-fill (not on drag-drop where the user
      // explicitly chose a destination).
      scrollToNextEmptySlot();
    }

    // Bring the next empty slot into view. Handles both layouts:
    //  - Collapsed mobile strip: scrolls horizontally, parking the slot
    //    ~16px from the left of the visible area.
    //  - Expanded grid: scrolls vertically (when the grid overflows its
    //    50vh cap), parking the slot ~8px from the top.
    // If already visible or no empties, does nothing.
    function scrollToNextEmptySlot() {
      const emptySlot = els.slots.querySelector('.byo-slot[data-byo-empty]');
      if (!emptySlot) return;
      const slotRect = emptySlot.getBoundingClientRect();
      const containerRect = els.slots.getBoundingClientRect();
      const collapsed = els.tray.classList.contains('byo-tray--collapsed');

      // Same scroll-snap trick as edge-auto-scroll: smooth scroll is
      // rejected by `scroll-snap-type: x mandatory`, so temporarily
      // disable snap, animate, restore once landed.
      const prevSnap = els.slots.style.scrollSnapType;
      els.slots.style.scrollSnapType = 'none';

      if (collapsed) {
        // Horizontal strip
        if (slotRect.left >= containerRect.left &&
            slotRect.right <= containerRect.right) {
          els.slots.style.scrollSnapType = prevSnap; // nothing to do
          return;
        }
        const offset = 16;
        const targetLeft = els.slots.scrollLeft + (slotRect.left - containerRect.left) - offset;
        try {
          els.slots.scrollTo({ left: targetLeft, behavior: 'smooth' });
        } catch (e) {
          els.slots.scrollLeft = targetLeft;
        }
      } else {
        // Expanded grid (vertical)
        if (slotRect.top >= containerRect.top &&
            slotRect.bottom <= containerRect.bottom) {
          els.slots.style.scrollSnapType = prevSnap;
          return;
        }
        const offset = 8;
        const targetTop = els.slots.scrollTop + (slotRect.top - containerRect.top) - offset;
        try {
          els.slots.scrollTo({ top: targetTop, behavior: 'smooth' });
        } catch (e) {
          els.slots.scrollTop = targetTop;
        }
      }

      setTimeout(() => { els.slots.style.scrollSnapType = prevSnap; }, 600);
    }

    function removeFromSlot(label) {
      if (label === 'cover') {
        state.cover = null;
      } else {
        const idx = Number(label) - 1;
        state.picks[idx] = null;
      }
      renderTray();
      refreshCardStates();
    }

    function clearAll() {
      state.picks = new Array(required).fill(null);
      if (hasCover) state.cover = null;
      // Wipe the saved set AND the "already in the cart" flag, so the next
      // build starts genuinely fresh and the button reads "Clear" again.
      state.addedToCart = false;
      state.addedSig = '';
      clearPersisted();
      renderTray();
      refreshCardStates();
    }

    // ----------- Data loading -----------

    // Set once the shared load-ahead engine is running. While it is, the
    // Load more button stays out of the way; if the engine gives up (or the
    // browser has no IntersectionObserver) it comes back automatically.


    /* One fetch, whole set. The builder used to imitate a server feed —
       page / hasMore / a slice per scroll — over data that was already
       entirely in memory. The design grids never did; they hold the filtered
       array and let the core page it, which is why their Load more only ever
       appeared when the set was genuinely exhausted. Same shape now. */
    /* The LATEST call wins, enforced by a token, not by refusing to start.
       The old `if (state.loading) return;` silently swallowed the second
       call: click collection A (index still downloading), back, click B —
       B's loadPage returned immediately, then A's promise resolved and
       painted A's designs into a panel labelled B. Now every call runs and
       a resolution that is no longer the newest throws its result away. */
    let loadSeq = 0;
    async function loadPage(reset) {
      const seq = ++loadSeq;
      state.loading = true;
      els.loading.hidden = false;
      try {
        const result = await BYO.data.fetchArtworks({
          collection: state.collection || '',
          sourceCollection: collection,
          query: state.query,
          artist: state.artist || [],
          orient: state.orient || '',
          sort: state.sort || '',
        });
        if (seq !== loadSeq) return; // superseded while fetching
        // The same curation the design pages apply: blocked never shows;
        // on the default Featured order, Joel's picks pin to the front and
        // the demote-list titles drop to the end. Explicit sorts already
        // placed everything naturally in the shared pipeline.
        let list = result.artworks.filter((a) => !blockedSet.has(curHandle(a.key)));
        if (!state.sort) {
          const pinSet = new Set(pickOrder);
          const pinned = [];
          pickOrder.forEach((h) => {
            const hit = list.find((a) => curHandle(a.key) === h);
            if (hit && pinned.indexOf(hit) === -1) pinned.push(hit);
          });
          const rest = [], demoted = [];
          list.forEach((a) => {
            if (pinSet.has(curHandle(a.key))) return;
            (isDemoted(a.title) ? demoted : rest).push(a);
          });
          list = pinned.concat(rest, demoted);
        }
        state.artworks = list;
        if (reset) state.firstLoad = false;
        renderGrid();
        renderChips();
        refreshCollectionOptions();
      } catch (err) {
        if (seq !== loadSeq) return;
        console.error('[BYO] fetch failed', err);
        els.empty.textContent = 'Could not load artwork. Try refreshing.';
        els.empty.hidden = false;
      } finally {
        if (seq === loadSeq) {
          state.loading = false;
          els.loading.hidden = true;
        }
      }
    }

    const onSearch = debounce(() => {
      state.query = els.search.value.trim();
      loadPage(true);
    }, 250);

    // ----------- Shared filter controls (Phase B step 3b) -----------
    // Artist / shape / sort, the same three the design grids offer. Each one
    // resets to page 1, because the pipeline filters the whole index before
    // slicing — changing a filter mid-scroll must not keep paging through the
    // previous result set.
    function onFilterChange(key, value) {
      state[key] = value;
      loadPage(true);
    }
    // "Add an artist": each pick becomes a chip, the dropdown snaps back to
    // the placeholder — identical to the design grids (one UX, 08-08).
    if (els.fArtist) els.fArtist.addEventListener('change', () => {
      const v = els.fArtist.value;
      const cur = Array.isArray(state.artist) ? state.artist : (state.artist ? [state.artist] : []);
      if (v && cur.indexOf(v) === -1) state.artist = cur.concat(v);
      els.fArtist.value = '';
      loadPage(true);
    });
    if (els.fOrient) els.fOrient.addEventListener('change', () => onFilterChange('orient', els.fOrient.value));
    if (els.fSort)   els.fSort.addEventListener('change',   () => onFilterChange('sort',   els.fSort.value));

    // The chips read/write BYO state through this adapter (its search key
    // is `query`, the shared vocabulary says `q`), and removing a chip
    // re-syncs the row and refilters — one renderer for every surface.
    const chipState = {
      get q() { return state.query; },       set q(v) { state.query = v; },
      get artist() { return state.artist; }, set artist(v) { state.artist = v; },
      get orient() { return state.orient; }, set orient(v) { state.orient = v; },
    };
    function renderChips() {
      if (!els.chips || !window.ADGFilters) return;
      window.ADGFilters.renderChips(
        els.chips,
        { q: els.search, artist: els.fArtist, orient: els.fOrient },
        chipState,
        () => { syncFilterRow(); loadPage(true); }
      );
    }

    // The row's controls are built from the artworks in the CURRENT
    // collection, not the whole index — and the rules for what each control
    // offers (and when it hides) are the SHARED rulebook,
    // ADGFilters.populateRow, the same call the design grids make. A
    // selection the new scope cannot honour comes back cleared, and this
    // builder adopts what it gets. Re-run whenever the collection changes.
    async function syncFilterRow() {
      if (!window.ADGFilters || !BYO.data.allArtworks) return;
      const all = await BYO.data.allArtworks();
      if (!all) return;
      const scope = state.collection
        ? window.ADGFilters.filterAndSort(all, { collection: state.collection })
        : all;
      state.scopeCount = scope.length;
      const norm = window.ADGFilters.populateRow(
        {
          q: els.search, qWrap: els.searchWrap,
          artist: els.fArtist, artistWrap: els.fArtistWrap,
          orient: els.fOrient, orientWrap: els.fOrientWrap,
          sort: els.fSort, sortWrap: els.fSortWrap,
        },
        scope,
        { q: state.query, artist: state.artist, orient: state.orient, sort: state.sort }
      );
      state.artist = norm.artist;
      state.orient = norm.orient;
      state.sort = norm.sort;
      state.query = norm.q;
    }
    syncFilterRow();

    // ----------- Submit bar spacing -----------

    // The fixed submit bar overlays page content (Horizon footer etc).
    // Dynamically add bottom padding to the page so the last bit of content
    // can scroll up above the bar.
    function setupSubmitBarSpacing() {
      if (!els.submitBar) return;
      const update = () => {
        const rect = els.submitBar.getBoundingClientRect();
        // Total space the bar occupies from the viewport bottom edge
        const spaceFromBottom = Math.max(0, window.innerHeight - rect.top);
        // Add a touch of breathing room above the bar
        document.body.style.paddingBottom = (spaceFromBottom + 12) + 'px';
      };
      update();
      // Recompute when the bar size changes (button label changes, variant
      // selector resizes, etc.)
      if (typeof ResizeObserver !== 'undefined') {
        new ResizeObserver(update).observe(els.submitBar);
      }
      window.addEventListener('resize', update);
      window.addEventListener('orientationchange', update);
    }

    // ----------- Collection filter dropdown -----------
    // Pulls collection options from REAL loaded artwork data (each
    // artwork's .collections array, derived from product `collection:*`
    // tags). Always-present "All Collections" option first. The list
    // grows naturally as more artworks load. An optional section setting
    // collection_filter_options can OVERRIDE this with a hand-curated
    // list (one per line, 'handle|Display Name') — useful for ordering
    // or hiding obscure collections.
    function parseOverrideOptions(raw) {
      if (!raw) return null;
      const items = raw.split(/\r?\n/).map((s) => s.trim()).filter(Boolean).map((line) => {
        const [handle, label] = line.split('|').map((t) => (t || '').trim());
        return { handle: handle || 'all', label: label || handle || 'All Design Collections' };
      });
      return items.length ? items : null;
    }

    function handleToTitle(handle) {
      // big-bend-national-park → Big Bend National Park
      return handle.split('-')
        .map((w) => w ? w[0].toUpperCase() + w.slice(1) : w)
        .join(' ');
    }

    function collectionOptionsFromData() {
      // Gather unique collection handles from loaded artworks
      const seen = new Set();
      state.artworks.forEach((a) => {
        (a.collections || []).forEach((h) => { if (h) seen.add(h); });
      });
      const handles = Array.from(seen).sort();
      const opts = [{ handle: 'all', label: 'All Design Collections' }];
      handles.forEach((h) => opts.push({ handle: h, label: handleToTitle(h) }));
      return opts;
    }

    function setupCollectionFilter() {
      if (!els.collectionToggle || !els.collectionMenu) return;

      const override = parseOverrideOptions(root.dataset.collectionOptions);
      // If override is just the default starter ("all|All Collections" + a few
      // placeholders), the merchant hasn't customized it — prefer real data.
      // Heuristic: treat override as "use it" only when it's not the default.
      const looksDefault = override && override.length === 5 &&
        override[0].handle === 'all' &&
        override.some((o) => o.handle === 'national-parks');

      function setMenuOpen(open) {
        els.collectionMenu.hidden = !open;
        els.collectionToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        els.collectionWrap.classList.toggle('byo-tray__collection--open', open);
      }

      function selectCollection(opt) {
        state.collectionFilter = opt.handle;
        els.collectionLabel.textContent = opt.label;
        setMenuOpen(false);
        renderGrid();
      }

      function renderMenu(options) {
        els.collectionMenu.innerHTML = '';
        options.forEach((opt) => {
          const li = document.createElement('li');
          li.className = 'byo-tray__collection-opt';
          li.setAttribute('role', 'option');
          li.dataset.handle = opt.handle;
          li.textContent = opt.label;
          if (opt.handle === state.collectionFilter) {
            li.setAttribute('aria-selected', 'true');
          }
          li.addEventListener('click', () => selectCollection(opt));
          els.collectionMenu.appendChild(li);
        });
      }

      // Expose so loadPage can call us after new data arrives
      refreshCollectionOptions = function () {
        const opts = (override && !looksDefault) ? override : collectionOptionsFromData();
        if (opts.length <= 1) {
          // Only "All Collections" with no real categories — hide the control
          if (els.collectionWrap) els.collectionWrap.hidden = true;
        } else {
          if (els.collectionWrap) els.collectionWrap.hidden = false;
          renderMenu(opts);
        }
      };
      refreshCollectionOptions();

      els.collectionToggle.addEventListener('click', (e) => {
        e.stopPropagation();
        setMenuOpen(els.collectionMenu.hidden);
      });

      document.addEventListener('click', (e) => {
        if (!els.collectionWrap.contains(e.target)) setMenuOpen(false);
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !els.collectionMenu.hidden) {
          setMenuOpen(false);
          els.collectionToggle.focus();
        }
      });
    }

    // Stub for refreshCollectionOptions in case setup wasn't called yet
    let refreshCollectionOptions = function () {};

    // ----------- Mailing list popover (hero overlay) -----------
    // Hidden by default in HTML (hidden attr). JS unhides on init unless
    // the customer previously dismissed it (tracked in localStorage so it
    // doesn't pester them across page loads). Survives form re-submits;
    // Liquid handles success/error message rendering.
    const MAILING_DISMISS_KEY = 'byo:mailing-popover:dismissed';
    function setupMailingPopover() {
      const pop = root.querySelector('[data-byo-mailing]');
      if (!pop) return;
      let dismissed = false;
      try { dismissed = localStorage.getItem(MAILING_DISMISS_KEY) === '1'; } catch (e) {}
      if (dismissed) return; // keep hidden
      pop.hidden = false;
      const closeBtn = pop.querySelector('[data-byo-mailing-close]');
      if (closeBtn) {
        closeBtn.addEventListener('click', () => {
          pop.hidden = true;
          try { localStorage.setItem(MAILING_DISMISS_KEY, '1'); } catch (e) {}
        });
      }
    }

    // ----------- Collapse / expand toggle -----------

    const MOBILE_BREAKPOINT = 768;

    function setupToggle() {
      if (!els.toggleBtn) return;
      // Initial state is determined by viewport width, period:
      //   - Mobile (<768): collapsed (scrollable strip)
      //   - Desktop (>=768): expanded (grid)
      // No persisted preference is honored — user complaint was that
      // mobile loads sometimes showed the laid-out grid instead of the
      // strip. Always normalize on load.
      setCollapsed(isMobileWidth(), /*userInitiated*/ false);

      els.toggleBtn.addEventListener('click', () => {
        setCollapsed(!els.tray.classList.contains('byo-tray--collapsed'), true);
      });

      // When the viewport crosses the breakpoint (resize / device rotation /
      // dev-tools toggle), snap the tray back to the appropriate view so the
      // user never sees the wrong layout for their current width.
      let lastIsMobile = isMobileWidth();
      window.addEventListener('resize', () => {
        const nowMobile = isMobileWidth();
        if (nowMobile !== lastIsMobile) {
          lastIsMobile = nowMobile;
          setCollapsed(nowMobile, /*userInitiated*/ false);
        }
      });
    }

    function isMobileWidth() {
      return window.innerWidth < MOBILE_BREAKPOINT;
    }

    function setCollapsed(collapsed, userInitiated) {
      els.tray.classList.toggle('byo-tray--collapsed', collapsed);
      els.toggleBtn.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
      els.toggleBtn.setAttribute('aria-label', collapsed ? 'Expand picks' : 'Collapse picks');
      // No localStorage persistence — we always derive from viewport width
      // so the layout matches the current device on every load.
      requestAnimationFrame(updateOverflowIndicator);
    }

    function updateOverflowIndicator() {
      if (!els.slots) return;
      const overflows = els.slots.scrollWidth > els.slots.clientWidth + 1;
      els.tray.classList.toggle('byo-tray--has-overflow', overflows);
    }

    // Re-check fade on resize and on scroll-end (hide fade when scrolled to end)
    window.addEventListener('resize', updateOverflowIndicator);
    if (els.slots) {
      els.slots.addEventListener('scroll', () => {
        const atEnd = els.slots.scrollLeft + els.slots.clientWidth >= els.slots.scrollWidth - 4;
        els.tray.classList.toggle('byo-tray--scrolled-end', atEnd);
      });
    }

    // ----------- Preview modal -----------

    // Tile labels per product kind. Add a new kind here to support a new BYO product.
    const TILE_LABELS = {
      calendar:  ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'],
      notecards: null,  // null → falls back to "Card N"
      postcards: null,
    };

    function getTileLabel(kind, index) {
      const labels = TILE_LABELS[kind];
      if (labels && labels[index]) return labels[index];
      return `Card ${index + 1}`;
    }

    // Feature-detect <dialog>.showModal — Safari got it in 15.4 (March
    // 2022). Older Safari, iOS 14, and very old Firefox don't have it.
    // When unavailable we synthesize modal behavior: backdrop, body scroll
    // lock, ESC to close, focus inside the dialog.
    const HAS_NATIVE_DIALOG =
      typeof HTMLDialogElement !== 'undefined' &&
      typeof HTMLDialogElement.prototype.showModal === 'function';

    let _prevBodyOverflow = '';
    let _prevActiveElement = null;
    let _escHandler = null;

    function setupPreviewModal() {
      if (!els.previewModal) return;
      // DELEGATED, not bound to the button we captured at boot. renderTray()
      // rebuilds the tray markup, which throws that node away and puts a new
      // Preview button in its place — so the listener was sitting on an
      // orphan and the real button did nothing. That is why the preview modal
      // "opened with no tiles": it was never opening, renderPreview never
      // ran, and what looked open was a dialog left open from earlier.
      // Delegation survives every re-render, including ones added later.
      root.addEventListener('click', (e) => {
        const t = e.target;
        if (!t || !t.closest) return;
        if (t.closest('[data-byo-preview]')) { openPreview(); return; }
        if (t.closest('[data-byo-preview-close]')) { closePreview(); }
      }, true);
      // Click backdrop to close (works for both native + fallback)
      els.previewModal.addEventListener('click', (e) => {
        if (e.target === els.previewModal) closePreview();
      });
      // Native dialog also fires `close` when ESC is pressed; reset state.
      els.previewModal.addEventListener('close', onModalClosed);
      // Preview's Add to Cart triggers the same action and closes the modal
      if (els.previewAdd) {
        els.previewAdd.addEventListener('click', () => {
          closePreview();
          addToCart();
        });
      }
    }

    function openPreview() {
      renderPreview();
      // Lock body scroll while modal is up (both code paths).
      _prevBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      _prevActiveElement = document.activeElement;

      if (HAS_NATIVE_DIALOG) {
        els.previewModal.showModal();
      } else {
        // Fallback: manually open + ESC handler + custom backdrop via class.
        els.previewModal.setAttribute('open', '');
        els.previewModal.classList.add('byo-preview--fallback-open');
        _escHandler = (e) => { if (e.key === 'Escape') closePreview(); };
        document.addEventListener('keydown', _escHandler);
      }
      // Move focus into the dialog for keyboard users (a11y).
      const focusTarget = els.previewModal.querySelector(
        '[data-byo-preview-close], button, [href], [tabindex]:not([tabindex="-1"])'
      );
      if (focusTarget) {
        try { focusTarget.focus({ preventScroll: true }); } catch (e) { focusTarget.focus(); }
      }
    }

    function closePreview() {
      if (HAS_NATIVE_DIALOG && typeof els.previewModal.close === 'function') {
        els.previewModal.close(); // fires `close` event → onModalClosed
      } else {
        els.previewModal.removeAttribute('open');
        els.previewModal.classList.remove('byo-preview--fallback-open');
        onModalClosed();
      }
    }

    function onModalClosed() {
      document.body.style.overflow = _prevBodyOverflow;
      if (_escHandler) {
        document.removeEventListener('keydown', _escHandler);
        _escHandler = null;
      }
      // Restore focus to whatever was active before opening.
      if (_prevActiveElement && typeof _prevActiveElement.focus === 'function') {
        try { _prevActiveElement.focus({ preventScroll: true }); } catch (e) {}
        _prevActiveElement = null;
      }
    }

    // ----------- Artwork lightbox -----------
    // The ⤢ icon on any card opens the SHARED viewer (adg-lightbox.js) — the
    // same component the design grids use. BYO carried a full reimplementation
    // of it until 2026-08-05; this adapter is all that was ever BYO-specific:
    // mapping index rows to viewer items and choosing what list to page.
    //
    // Paging steps through state.artworks, which IS the current filtered
    // view, so it never wanders outside the collection or filters in effect.
    // When the artwork is not in that list (opened from a filled slot, or
    // filtered out since it was picked) the viewer gets a one-item list and
    // draws no arrows, rather than jumping somewhere arbitrary.
    // Capability flag (REBUILD-PLAN decision 5): paging on by default; a
    // surface that should not page sets data-lightbox-paging="false" on the
    // builder root.
    const LIGHTBOX_PAGING = root.dataset.lightboxPaging !== 'false';

    function lightboxItem(a) {
      // No href on purpose: the design grids' CTA navigates to the product,
      // and leaving the builder mid-pick is exactly what BYO must not do.
      return { image: a.image_full || a.image, title: a.title, artist: a.artist_name };
    }

    function openArtworkLightbox(art) {
      if (!window.ADGLightbox) return;
      const list = state.artworks || [];
      const at = list.findIndex((a) => pickKey(a) === pickKey(art));

      // "Shouldn't you be able to tap that as you go through?" — yes. The
      // viewer's action button ADDS the design on show, same semantics as
      // tapping its card (duplicates allowed; removal stays in the tray).
      // state() is re-asked after every add, so the label carries the live
      // count and flips to "set is full" the moment it is.
      const total = required + (hasCover ? 1 : 0);
      const action = {
        state: function () {
          const canAdd = (hasCover && !state.cover) || state.picks.some((p) => p === null);
          const filled = state.picks.filter(Boolean).length + (hasCover && state.cover ? 1 : 0);
          if (!canAdd) return { label: 'Your set is full — remove one in the tray', disabled: true };
          // "Add another" once something is in — the words themselves say
          // the last tap worked, even if the eye missed the count.
          return { label: (filled > 0 ? 'Add another' : 'Add to my picks') + ' · ' + filled + ' of ' + total };
        },
        onAct: function (item, i) {
          const canAdd = (hasCover && !state.cover) || state.picks.some((p) => p === null);
          if (!canAdd) return false;               // nothing happened — no beat
          handleCardClick(at < 0 ? art : (list[i] || art));
          return true;
        }
      };

      if (!LIGHTBOX_PAGING || at < 0) {
        window.ADGLightbox.open([lightboxItem(art)], 0, { action: action });
      } else {
        window.ADGLightbox.open(list.map(lightboxItem), at, { action: action });
      }
    }


    function renderPreview() {
      // Show / hide cover section based on hasCover config
      const coverSection = els.previewModal.querySelector('.byo-preview__cover-section');
      if (coverSection) {
        coverSection.style.display = hasCover ? '' : 'none';
        if (hasCover) renderPreviewTile(els.previewCover, state.cover, 'Cover', true);
      }
      // Render picks using required count (not hardcoded 12) + kind-aware labels
      els.previewGrid.innerHTML = '';
      const kind = root.dataset.kind || 'calendar';
      for (let i = 0; i < required; i++) {
        const tile = document.createElement('div');
        tile.className = 'byo-preview__tile';
        renderPreviewTile(tile, state.picks[i], getTileLabel(kind, i), false);
        els.previewGrid.appendChild(tile);
      }
    }

    function renderPreviewTile(container, item, label, isCover) {
      if (!container) return;
      container.classList.toggle('byo-preview__tile--empty', !item);
      container.innerHTML = '';
      const media = document.createElement('div');
      media.className = 'byo-preview__tile-media';
      if (item) {
        const img = document.createElement('img');
        img.src = item.image;
        img.alt = item.title;
        media.appendChild(img);
      } else {
        const empty = document.createElement('span');
        empty.className = 'byo-preview__tile-empty';
        empty.textContent = label;
        media.appendChild(empty);
      }
      container.appendChild(media);
      const lbl = document.createElement('span');
      lbl.className = 'byo-preview__tile-label';
      lbl.textContent = label;
      container.appendChild(lbl);
    }

    // ----------- Cart submission -----------

    function buildLineItemProperties() {
      const visibleProps = {};
      // Cart properties use full month names for calendars (friendly in order
      // confirmation), and "Card N" for notecards/postcards.
      const cartLabels = {
        calendar: ['January','February','March','April','May','June',
                   'July','August','September','October','November','December'],
      };
      const kind = root.dataset.kind || 'calendar';
      if (hasCover && state.cover) visibleProps['Cover'] = state.cover.title;
      state.picks.forEach((p, i) => {
        if (!p) return;
        const list = cartLabels[kind];
        const label = (list && list[i]) ? list[i] : `Card ${i + 1}`;
        visibleProps[label] = p.title;
      });

      const selections = {
        v: 1,
        type: root.dataset.kind,
        cover: state.cover ? { artist: state.cover.artist_handle, artwork: state.cover.artwork_handle, shopify_id: state.cover.shopify_id, title: state.cover.title } : null,
        picks: state.picks.filter(Boolean).map((p, i) => ({
          slot: i + 1,
          artist: p.artist_handle,
          artwork: p.artwork_handle,
          shopify_id: p.shopify_id,
          title: p.title,
        })),
      };

      return {
        ...visibleProps,
        _byo_type: root.dataset.kind,
        _byo_version: '1',
        _selections: JSON.stringify(selections),
      };
    }

    async function addToCart() {
      const variantId = els.variant && els.variant.value
        ? els.variant.value
        : root.dataset.variantId;
      const properties = buildLineItemProperties();

      els.addBtn.disabled = true;
      els.addLabel.textContent = 'Adding…';
      if (els.addHint) els.addHint.hidden = true;   // transient state owns the button alone

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: Number(variantId), quantity: 1, properties }],
          }),
        });
        if (!res.ok) throw new Error(`Cart returned ${res.status}`);
        // Picks intentionally SURVIVE add-to-cart (Jul 16 meeting): a customer who
        // goes back to tweak one design shouldn't lose the whole set. Record that
        // this set reached the cart so that on return the Clear button reads
        // "Build Another" — the deliberate way to start the next one.
        markAddedToCart();
        window.location.href = '/cart';
      } catch (err) {
        console.error('[BYO] add to cart failed', err);
        els.addLabel.textContent = 'Try again';
        els.addBtn.disabled = false;
        if (els.addHint) els.addHint.hidden = true;
      }
    }

    // ----------- Wire events -----------

    // Guarded like the other three: the shared row renders search only when
    // asked (adg-filter-row `search` param), so this element is optional —
    // and an unguarded bind here crashed the whole boot when the served
    // markup predated the shared row (2026-08-05, the "builders won't open").
    if (els.search) els.search.addEventListener('input', onSearch);
    els.clear.addEventListener('click', clearAll);
    els.loadmore.addEventListener('click', () => gridCore.more());
    els.addBtn.addEventListener('click', addToCart);
    setupSlotDragAndDrop();
    setupPreviewModal();
    setupToggle();
    setupCollectionFilter();
    setupMailingPopover();
    setupSubmitBarSpacing();
    if (els.variant && els.variant.tagName === 'SELECT') {
      els.variant.addEventListener('change', () => {
        const opt = els.variant.options[els.variant.selectedIndex];
        if (opt && opt.dataset.price && els.price) els.price.textContent = opt.dataset.price;
      });
    }

    // Returning via the browser Back button restores the page from the bfcache
    // with the DOM exactly as it was left — which meant the CTA stayed stuck on
    // "Adding…" and disabled after an add-to-cart. Recompute it on restore.
    window.addEventListener('pageshow', function (e) {
      if (e.persisted) renderTray();
    });

    // ----------- Load ahead of the scroll -----------
    // Nothing here any more: paging, the auto-load budget, the Load more
    // fallback and scroll memory all live in adg-grid-core, created above and
    // shared with the design grids. That is the point — this used to be a
    // second wiring of the same engine, and it was the one that measured
    // progress by counting DOM nodes, which is why Load more kept surfacing
    // mid-collection on the builders and never on the design pages.

    // ----------- Collection-first flow (Joel item A, TODO 11c) -----------
    //
    // The builder used to open straight into a flat grid of 30 arbitrary
    // artworks. Joel's words: it "dumps you into just everything with a
    // little way to find a collection". So now the page opens on the
    // collection tiles and the design picker does not exist yet.
    //
    // The tiles are the SAME adg-categories section used on the collection
    // pages, added to the BYO product templates — not a copy. This code only
    // decides which of the two panels is on screen.
    //
    // Nathan specified the interaction in the meeting (1:34–1:38): the picks
    // tray and empty boxes stay pinned, only the area below swaps, and a
    // small back arrow returns you to collections.
    // ---- Placement shim: move the tiles section into the slot ----
    //
    // PLACEMENT ONLY. This moves an existing DOM node; it renders nothing.
    // The tile markup itself is snippets/adg-collage.liquid (Phase A step 1),
    // and the collage panes are built by adg-cat-tiles.js.
    //
    // Why a shim is unavoidable, rather than laziness: Shopify renders each
    // section as a sibling <div class="shopify-section">, and template
    // `order` can only sequence those siblings — it cannot place one INSIDE
    // another section's markup. The tiles have to land between the picks tray
    // and the design grid, which are both inside the builder section, so no
    // ordering of sections can express it.
    //
    // The alternative would be giving the builder its own tile blocks, which
    // means a second copy of Joel's 40 collection blocks to keep in sync with
    // the ones the collection pages use. Strictly worse. Extracting the
    // snippet did NOT remove the need for this — the constraint is about
    // where a section's markup can live, not about who owns the markup.
    //
    // Idempotent (the contains() guard) and silent when either side is
    // missing, so a BYO template without the tiles section still boots.
    const tilesSlot = root.querySelector('[data-byo-tiles-slot]');
    const tilesSection = document.querySelector('.adg-cats')?.closest('.shopify-section');
    if (tilesSlot && tilesSection && !tilesSlot.contains(tilesSection)) {
      tilesSlot.appendChild(tilesSection);
    }

    const tiles = tilesSection || null;
    const backBtn = root.querySelector('[data-byo-back]');
    // The SHARED bar already decided, at its boot, whether this page has a
    // step UP to offer (adg-action-bar.js canStepUp — the one rule every
    // back control answers to; deferred scripts run before DOMContentLoaded,
    // so its verdict is in by the time we get here). The builder never
    // re-derives it: it captures the verdict and restores it whenever the
    // shopper is back at the collections step.
    const backBootHidden = backBtn ? backBtn.hidden : true;
    const browser = root.querySelector('.byo-browser');
    // Where the shopper was in the tiles when they picked one. This journey
    // never navigates — the panel swaps in place — so there is no page load
    // for the shared scroll memory to hook, and a plain variable is both
    // enough and honest. Restored when they come back.
    let tilesScrollY = 0;

    function showTiles() {
      if (!tiles) return;
      tiles.hidden = false;
      if (browser) browser.hidden = true;
      // The collections step is the builder's front door — from here "back"
      // means LEAVE, one step up the shopper's real path (Justin, 08-08: a
      // back button everywhere except home; he landed here from Wall
      // Calendars and had no way back). Visibility is the shared bar's boot
      // verdict, restored — not a second copy of the rule.
      if (backBtn) {
        backBtn.hidden = backBootHidden;
        const label = backBtn.querySelector('.adg-actionbar__label');
        if (label) label.textContent = 'Back';
        backBtn.setAttribute('aria-label', 'Back to the previous page');
      }
      // Nothing to hide any more: search and the filters live inside
      // .byo-browser now, which is already hidden during this step.
      root.dataset.byoStep = 'collections';
    }

    function showDesigns(handle, title) {
      if (tiles) tiles.hidden = true;
      if (browser) browser.hidden = false;
      if (backBtn) {
        backBtn.hidden = false;
        const label = backBtn.querySelector('.adg-actionbar__label');
        if (label) label.textContent = 'Collections';
        backBtn.setAttribute('aria-label',
          title ? `Back to all collections, leaving ${title}` : 'Back to all collections');
      }
      root.dataset.byoStep = 'designs';
      // SET-KEYED STATE (the contract): a DIFFERENT set starts clean — the
      // search box included. A query carried across collections starves the
      // new grid ("bigfoot" in State Pride → 0 of 59 → switch → still
      // "bigfoot": reads as "it didn't switch", reproduced live 08-07).
      // The SAME set keeps what the shopper had — the round trip back into
      // it restores rather than wipes. Everything resets synchronously,
      // BEFORE loadPage reads the state; syncFilterRow only rebuilds the
      // options and counts afterwards.
      const switched = (handle || '') !== (state.collection || '');
      state.collection = handle || '';
      if (switched) {
        state.query = ''; state.artist = []; state.orient = ''; state.sort = '';
        if (els.search)  els.search.value = '';
        if (els.fArtist) els.fArtist.value = '';
        if (els.fOrient) els.fOrient.value = '';
        if (els.fSort)   els.fSort.value = '';
      }
      syncFilterRow();
      loadPage(true);
    }

    if (tiles) {
      // The tile is an <a> to /collections/<handle> for every other page on
      // the site. In here it must filter in place instead of navigating away
      // and losing the shopper's picks.
      tiles.addEventListener('click', (e) => {
        const card = e.target.closest('.adg-cat[data-collection]');
        if (!card) return;
        e.preventDefault();
        const handle = card.dataset.collection;
        const title = (card.querySelector('.adg-cat__title')?.textContent || '').trim();
        tilesScrollY = Math.round(window.pageYOffset || 0);
        showDesigns(handle, title);
        // Land on the picker, not wherever the tile happened to be — and not
        // UNDER what sits pinned above it. Inside the builder that is our
        // OWN sticky picks tray (219px of it), not the site header; the old
        // scrollIntoView pinned the grid to the viewport top and the first
        // row arrived half-hidden behind the tray (Justin, 08-05).
        //
        // Two subtleties, both learned by measuring:
        //  - the tray's STUCK state cannot be sampled here (the panel swap
        //    just collapsed the page and the scroll clamps), but its HEIGHT
        //    is the same stuck or not — so use the height;
        //  - wait two frames, like the back-restore above, so the swapped
        //    layout exists before anything is measured or scrolled.
        const target = browser || root;
        requestAnimationFrame(() => requestAnimationFrame(() => {
          let offset = 0;
          const tray = root.querySelector('.byo-tray');
          if (tray) offset = tray.getBoundingClientRect().height;
          const topY = target.getBoundingClientRect().top + window.pageYOffset - offset - 10;
          window.scrollTo({ top: Math.max(0, topY), behavior: 'smooth' });
        }));
      });

      // adg-action-bar.js owns the click and fires this; we decide what
      // "back" means here. Inside the builder it must NOT navigate — that
      // would throw away the shopper's picks — it swaps the panel instead.
      root.addEventListener('adg-back-to-collections', (e) => {
        e.preventDefault();
        // Two meanings, by step: in the designs step back is the in-place
        // swap that keeps the picks alive; at the collections step it
        // walks UP the real path — a true history pop, so it can never
        // start a loop and the picks (in localStorage) survive regardless.
        if (root.dataset.byoStep !== 'designs') {
          if (window.history.length > 1) window.history.back();
          return;
        }
        showTiles();
        // Back where they were, not the top of a band they already scrolled
        // past. This line used to be an unconditional scrollIntoView, which
        // is why the position was never kept no matter what else was fixed.
        // Two frames: the tiles were display:none, so the document has to
        // regain its height before there is anywhere to scroll to.
        const y = tilesScrollY;
        requestAnimationFrame(() => requestAnimationFrame(() => {
          const max = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
          window.scrollTo(0, Math.min(y, max));
        }));
      });
    }

    // ----------- Initial load -----------
    renderTray();
    if (tiles) {
      // No artwork request at all until a collection is chosen — this is the
      // whole point of the change, not just a visual reorder.
      showTiles();
    } else {
      loadPage(true);
    }
  }

  function boot() {
    document.querySelectorAll('[data-byo-root]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  global.BYO = global.BYO || {};
  global.BYO.init = init;
})(window);
